import React, { useState, useEffect, useMemo } from "react"
import { Card, Modal, Label, Input } from "reactstrap"
import Select from "react-select"
import Swal from "sweetalert2"
import Loader from "pages/Separate/Loader"
import { fetchNodeData, FetchNodePostData } from "utils/common-fetchData"
import secureLocalStorage from "react-secure-storage"
import moment from "moment"
import { formatDateTime } from "utils/formatDate"
import AssessmentPopulationTable from "./AssessmentPopulationTable"

const EMPTY_TRAINEE_FORM = {
  name: "",
  email: "",
  phone: "",
  password: "",
  tier: "external",
  exams: [],
  sops: [],
}

const PASS_SCORE = 70
const EXCELLENCE_SCORE = 90

// ── Score helpers ─────────────────────────────────────────────────────────────
function calcScore(answers = []) {
  let earned = 0, maxPossible = 0
  let mcqCorrect = 0, mcqTotal = 0
  let tcEarned = 0, tcMax = 0, tcGraded = 0, tcTotal = 0, tcPending = 0

  for (const a of answers) {
    if (a.type === 1) {
      mcqTotal++
      maxPossible += 1
      if (a.isCorrect) { earned += 1; mcqCorrect++ }
    } else if (a.type === 2 || a.type === 3) {
      tcTotal++
      const ms = a.max_score || 10
      const effective = a.reviewer_score != null ? a.reviewer_score
        : (a.ai_score != null ? a.ai_score : null)
      if (effective !== null) {
        tcGraded++
        maxPossible += ms
        tcMax += ms
        earned += effective
        tcEarned += effective
      } else {
        tcPending++
      }
    }
  }

  const overallPct = maxPossible > 0 ? Math.round((earned / maxPossible) * 100) : 0
  const mcqPct = mcqTotal > 0 ? Math.round((mcqCorrect / mcqTotal) * 100) : 0

  return {
    mcqCorrect, mcqTotal, mcqPct,
    tcEarned, tcMax, tcGraded, tcTotal, tcPending,
    total: answers.length, overallPct,
    manualTotal: tcTotal, manualGraded: tcGraded, pendingManual: tcPending,
  }
}

function scoreColor(s) {
  if (s >= EXCELLENCE_SCORE) return "#16a34a"
  if (s >= PASS_SCORE) return "#d97706"
  return "#dc2626"
}
function scoreBg(s) {
  if (s >= EXCELLENCE_SCORE) return "#f0fdf4"
  if (s >= PASS_SCORE) return "#fffbeb"
  return "#fff5f5"
}
function scoreBorder(s) {
  if (s >= EXCELLENCE_SCORE) return "#86efac"
  if (s >= PASS_SCORE) return "#fde68a"
  return "#fecaca"
}
function scoreLeftBorder(s) {
  if (s >= EXCELLENCE_SCORE) return "#22c55e"
  if (s >= PASS_SCORE) return "#f59e0b"
  return "#ef4444"
}
function ringColor(s) {
  if (s >= EXCELLENCE_SCORE) return "#27ae60"
  if (s >= PASS_SCORE) return "#f39c12"
  return "#e74c3c"
}

function ScoreRing({ score, label }) {
  return (
    <div style={{ textAlign: "center", minWidth: 64 }}>
      <div style={{
        width: 60, height: 60, borderRadius: "50%", margin: "0 auto 4px",
        background: `conic-gradient(${ringColor(score)} ${score * 3.6}deg, #e9d5ff 0deg)`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: "50%", background: "#faf5ff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: 13, color: scoreColor(score),
        }}>{score}%</div>
      </div>
      <div style={{ fontSize: 11, color: "#752a97", fontWeight: 600 }}>{label}</div>
    </div>
  )
}


const ExternalCandidates = () => {
  const [mainTab, setMainTab] = useState("candidates") // "candidates" | "trainee"
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [search, setSearch] = useState("")
  const [filterTest, setFilterTest] = useState("")
  const [filterStatus, setFilterStatus] = useState("")
  const [filterViolations, setFilterViolations] = useState("")
  const [modal, setModal] = useState(false)
  const [selected, setSelected] = useState(null)
  const [detailAnswers, setDetailAnswers] = useState([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [activeDetailTab, setActiveDetailTab] = useState("overview")

  const [examOptions, setExamOptions] = useState([])
  const [sopOptions, setSopOptions] = useState([])
  const [traineeModal, setTraineeModal] = useState(false)
  const [traineeSource, setTraineeSource] = useState(null)
  const [traineeForm, setTraineeForm] = useState(EMPTY_TRAINEE_FORM)
  const [savingTrainee, setSavingTrainee] = useState(false)

  // "External Trainee" tab shares access control with the same tab in Overall
  // Assessment View — set in Settings > General Settings > Assessment View.
  const [traineeTabAllowed, setTraineeTabAllowed] = useState(null) // null = still checking

  useEffect(() => {
    const load = async () => {
      const token = secureLocalStorage.getItem("auth_token")
      // "Not in system" candidates only — large pageSize since this widget shows the
      // full list at once rather than paginating (volume is now excluded of trainees).
      const res = await fetchNodeData(setLoading, "preassesment/data/external?pageSize=5000", token)
      if (res?.success) setData(res.data || [])
    }
    load()

    const checkTraineeAccess = async () => {
      const token = secureLocalStorage.getItem("auth_token")
      const userId = localStorage.getItem("auth_id")
      const res = await fetchNodeData(null, "preassesment/settings/tabAccess", token)
      const rows = res?.success ? res.data || [] : []
      const row = rows.find(r => r.tab_key === "external_trainee")
      const allowed = !row || !row.is_active
        || (row.user_ids ? row.user_ids.split(",").map(s => s.trim()) : []).includes(String(userId))
      setTraineeTabAllowed(allowed)
    }
    checkTraineeAccess()
  }, [])

  useEffect(() => {
    const loadPickers = async () => {
      const token = secureLocalStorage.getItem("auth_token")
      const userId = localStorage.getItem("auth_id")
      const [examRes, sopRes] = await Promise.all([
        fetchNodeData(null, `preassesment/test/get/${userId}`, token),
        fetchNodeData(null, "sop/folderAccess", token),
      ])
      if (examRes?.success) {
        setExamOptions((examRes.data || []).map(t => ({
          value: t.id,
          label: `${t.test_name} (${Number(t.test_type) === 1 ? "Mock" : "Actual"})`,
        })))
      }
      if (sopRes?.success) {
        setSopOptions((sopRes.data || []).map(s => ({ value: s.id, label: s.folder_label })))
      }
    }
    loadPickers()
  }, [])

  const openCreateTrainee = row => {
    setTraineeSource(row)
    setTraineeForm({ ...EMPTY_TRAINEE_FORM, name: row.name || "", email: row.email || "" })
    setTraineeModal(true)
  }

  const handleCreateTrainee = async e => {
    e.preventDefault()
    if (!traineeForm.name.trim() || !traineeForm.email.trim() || !traineeForm.password) {
      Swal.fire({ icon: "warning", title: "Name, email and password are required", confirmButtonColor: "#752a97" })
      return
    }
    setSavingTrainee(true)
    const token = secureLocalStorage.getItem("auth_token")
    const userId = localStorage.getItem("auth_id")
    const res = await FetchNodePostData(setSavingTrainee, "trainee/create", token, {
      name: traineeForm.name.trim(),
      email: traineeForm.email.trim(),
      phone: traineeForm.phone || "",
      password: traineeForm.password,
      tier: traineeForm.tier,
      exam_ids: traineeForm.exams.map(o => o.value).join(","),
      sop_ids: traineeForm.sops.map(o => o.value).join(","),
      user_id: userId,
    })
    if (res?.data?.success) {
      Swal.fire({ icon: "success", title: "Trainee created successfully", timer: 1500, showConfirmButton: false })
      setTraineeModal(false)
    } else {
      Swal.fire({ icon: "error", title: "Failed to create trainee", text: res?.data?.message, confirmButtonColor: "#752a97" })
    }
  }

  const testOptions = useMemo(() => [...new Set(data.map(d => d.test_name))].sort(), [data])

  // Score is computed server-side now (answers are no longer shipped in the list
  // payload — they're lazy-fetched per-row only when the detail modal is opened).
  const enriched = useMemo(() =>
    data.map(row => ({ ...row, overallScore: row.score ?? 0 }))
  , [data])

  const filtered = useMemo(() => {
    return enriched.filter(row => {
      const q = search.toLowerCase()
      if (q && !row.name?.toLowerCase().includes(q) && !row.email?.toLowerCase().includes(q)) return false
      if (filterTest && row.test_name !== filterTest) return false
      if (filterStatus === "pass" && row.overallScore < PASS_SCORE) return false
      if (filterStatus === "fail" && row.overallScore >= PASS_SCORE) return false
      if (filterStatus === "excellent" && row.overallScore < EXCELLENCE_SCORE) return false
      if (filterViolations === "yes" && row.violation_count === 0) return false
      if (filterViolations === "no" && row.violation_count > 0) return false
      return true
    })
  }, [enriched, search, filterTest, filterStatus, filterViolations])

  const stats = useMemo(() => ({
    total: filtered.length,
    pass: filtered.filter(r => r.overallScore >= PASS_SCORE).length,
    fail: filtered.filter(r => r.overallScore < PASS_SCORE).length,
    avg: filtered.length ? Math.round(filtered.reduce((s, r) => s + r.overallScore, 0) / filtered.length) : 0,
    violations: filtered.filter(r => r.violation_count > 0).length,
  }), [filtered])

  const openDetail = async (row) => {
    setSelected(row)
    setDetailAnswers([])
    setActiveDetailTab("overview")
    setModal(true)
    const token = secureLocalStorage.getItem("auth_token")
    const res = await fetchNodeData(setDetailLoading, `preassesment/data/getResponse/detail/${row.id}`, token)
    if (res?.success) setDetailAnswers(res.data?.answers || [])
  }

  const hasFilters = search || filterTest || filterStatus || filterViolations

  return (
    <div>
      {isLoading && <Loader />}
      <Card>
        <div className="card-body">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-3 pb-2"
            style={{ borderBottom: "1.5px solid #f0e6f8" }}>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: 34, height: 34, borderRadius: 8,
                background: "linear-gradient(135deg, #752a97, #a855c9)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="fas fa-user-tie text-white" style={{ fontSize: 15 }}></i>
              </div>
              <span className="text-purple fw-bold" style={{ fontSize: 15 }}>External Candidates</span>
            </div>
            {data.length > 0 && (
              <span style={{ background: "#f3e8ff", color: "#752a97", border: "1px solid #d8b4fe", borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 600 }}>
                {data.length} Response{data.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Main tabs: External Candidates (always visible) | External Trainee (permission-gated) */}
          <ul className="nav nav-tabs mb-3" style={{ borderBottom: "2px solid #e9ecef" }}>
            {[
              { key: "candidates", label: "External Candidates" },
              ...(traineeTabAllowed !== false ? [{ key: "trainee", label: "External Trainee" }] : []),
            ].map(tab => (
              <li key={tab.key} className="nav-item">
                <button
                  className={`nav-link ${mainTab === tab.key ? "active fw-semibold" : "text-muted"}`}
                  style={{
                    border: "none",
                    borderBottom: mainTab === tab.key ? "3px solid #752a97" : "3px solid transparent",
                    color: mainTab === tab.key ? "#752a97" : undefined,
                    background: "transparent",
                    padding: "8px 20px",
                    fontSize: 14,
                  }}
                  onClick={() => setMainTab(tab.key)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>

          {mainTab === "trainee" ? (
            <AssessmentPopulationTable population="external_trainee" />
          ) : (
          <>
          {/* Stats + Filters */}
          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
            {data.length > 0 && (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {[
                  { label: "Total", value: stats.total, color: "#752a97", bg: "#f5eeff", icon: "fas fa-users" },
                  { label: "Passed", value: stats.pass, color: "#16a34a", bg: "#edfbf3", icon: "fas fa-check-circle" },
                  { label: "Failed", value: stats.fail, color: "#dc2626", bg: "#fff5f5", icon: "fas fa-times-circle" },
                ].map(({ label, value, color, bg, icon }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: `1.5px solid ${color}25`, borderRadius: 10, padding: "6px 12px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <i className={icon} style={{ color, fontSize: 11 }}></i>
                    </div>
                    <div style={{ lineHeight: 1.2 }}>
                      <div style={{ fontSize: 10, color: "#888", fontWeight: 500 }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color }}>{value}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Filters */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="position-relative" style={{ flex: "1 1 160px", maxWidth: 200 }}>
                <i className="fas fa-search position-absolute" style={{ left: 10, top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: 12 }}></i>
                <input
                  className="form-control"
                  style={{ paddingLeft: 30, fontSize: 13, borderRadius: 8, height: 36 }}
                  placeholder="Search name or email..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <select className="form-select border-radius" style={{ width: 150, fontSize: 13, height: 36 }}
                value={filterTest} onChange={e => setFilterTest(e.target.value)}>
                <option value="">All Tests</option>
                {testOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="form-select border-radius" style={{ width: 130, fontSize: 13, height: 36 }}
                value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="excellent">Excellent</option>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
              </select>
              <select className="form-select border-radius" style={{ width: 150, fontSize: 13, height: 36 }}
                value={filterViolations} onChange={e => setFilterViolations(e.target.value)}>
                <option value="">All Violations</option>
                <option value="yes">Has Violations</option>
                <option value="no">No Violations</option>
              </select>
              {hasFilters && (
                <button className="btn btn-sm rounded-4 btn-red"
                  onClick={() => { setSearch(""); setFilterTest(""); setFilterStatus(""); setFilterViolations("") }}>
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          {filtered.length === 0 && !isLoading ? (
            <div className="text-center text-muted py-4">
              <i className="fas fa-user-tie fa-2x mb-2 d-block"></i>
              {hasFilters ? "No candidates match the current filters." : "No external candidate responses yet."}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered mb-0" style={{ fontSize: 13 }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Candidate</th>
                    <th>Test</th>
                    <th className="text-center" style={{ width: 120 }}>Score</th>
                    <th className="text-center" style={{ width: 110 }}>Status</th>
                    <th className="text-center" style={{ width: 110 }}>Violations</th>
                    <th style={{ width: 150 }}>Submitted On</th>
                    <th className="text-center" style={{ width: 80 }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => {
                    const s = row.overallScore
                    return (
                      <tr key={row.id} style={{ background: scoreBg(s), borderLeft: `3px solid ${scoreLeftBorder(s)}` }}>
                        <td style={{ verticalAlign: "middle" }}>{i + 1}</td>
                        <td style={{ verticalAlign: "middle" }}>
                          <div style={{ fontWeight: 600, color: "#1f2937" }}>{row.name || "—"}</div>
                          <div style={{ fontSize: 11, color: "#6b7280" }}>{row.email}</div>
                        </td>
                        <td style={{ verticalAlign: "middle", fontWeight: 500 }}>{row.test_name}</td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          <div style={{ fontWeight: 800, fontSize: 15, color: scoreColor(s) }}>{s}%</div>
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: scoreBg(s), border: `1px solid ${scoreBorder(s)}`, color: scoreColor(s) }}>
                            {s >= EXCELLENCE_SCORE ? <><i className="fas fa-star me-1"></i>Excellent</> : s >= PASS_SCORE ? <><i className="fas fa-check me-1"></i>Pass</> : <><i className="fas fa-times me-1"></i>Fail</>}
                          </span>
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          {row.violation_count > 0 ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "#dc2626" }}>
                              <i className="fas fa-exclamation-triangle"></i> {row.violation_count}
                            </span>
                          ) : (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "#16a34a" }}>
                              <i className="fas fa-shield-alt"></i> Clean
                            </span>
                          )}
                        </td>
                        <td style={{ verticalAlign: "middle", color: "#555" }}>
                          {formatDateTime(row.created_at)}
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          <div className="d-flex gap-1 justify-content-center">
                            <button className="btn btn-sm" style={{ border: "1px solid #752a97", color: "#752a97", background: "transparent" }}
                              onClick={() => openDetail(row)} title="View Details">
                              <i className="fas fa-eye"></i>
                            </button>
                            <button className="btn btn-sm" style={{ border: "1px solid #16a34a", color: "#16a34a", background: "transparent" }}
                              onClick={() => openCreateTrainee(row)} title="Create Trainee">
                              <i className="fas fa-user-graduate"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          </>
          )}
        </div>
      </Card>

      {/* Detail Modal */}
      {selected && (() => {
        const sc = calcScore(detailAnswers)
        return (
          <Modal isOpen={modal} size="xl" centered toggle={() => setModal(false)}>
            <div className="modal-header pe-4">
              <div>
                <h5 className="modal-title mt-0">
                  <i className="fas fa-user-tie me-2 text-purple"></i>
                  {selected.name || selected.email}
                </h5>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {selected.email} &nbsp;·&nbsp; {selected.test_name} &nbsp;·&nbsp;
                  {formatDateTime(selected.created_at)}
                </div>
              </div>
              <button type="button" onClick={() => setModal(false)} className="btn-modal-close">
                <span aria-hidden="true" className="btn-modal-span">&times;</span>
              </button>
            </div>

            {/* Tabs */}
            <div className="d-flex gap-0" style={{ borderBottom: "1px solid #f0e6f8", padding: "0 20px" }}>
              {[
                { key: "overview", label: "Overview", icon: "fas fa-chart-pie" },
                { key: "answers", label: `Answers (${detailAnswers.length})`, icon: "fas fa-list-ol" },
                { key: "violations", label: `Violations (${selected.violation_count})`, icon: "fas fa-exclamation-triangle" },
              ].map(tab => (
                <button key={tab.key} type="button"
                  onClick={() => setActiveDetailTab(tab.key)}
                  style={{
                    padding: "10px 18px", border: "none", background: "transparent", cursor: "pointer",
                    fontSize: 13, fontWeight: 600,
                    color: activeDetailTab === tab.key ? "#752a97" : "#9ca3af",
                    borderBottom: activeDetailTab === tab.key ? "2px solid #752a97" : "2px solid transparent",
                    marginBottom: -1,
                  }}>
                  <i className={`${tab.icon} me-1`}></i>{tab.label}
                </button>
              ))}
            </div>

            <div className="modal-body p-4" style={{ maxHeight: "65vh", overflowY: "auto" }}>

              {/* ── Overview tab ── */}
              {activeDetailTab === "overview" && (
                <div>
                  <div className="d-flex align-items-center gap-4 p-3 mb-3 flex-wrap"
                    style={{ background: "#faf5ff", borderRadius: 10, border: "1px solid #e9d5ff" }}>

                    {/* Overall ring */}
                    <ScoreRing score={sc.overallPct} label="Overall" />

                    {/* Divider */}
                    {(sc.mcqTotal > 0 || sc.tcTotal > 0) && (
                      <div style={{ width: 1, height: 60, background: "#e9d5ff", flexShrink: 0 }} />
                    )}

                    {/* MCQ breakdown */}
                    {sc.mcqTotal > 0 && (
                      <div className="text-center" style={{ minWidth: 70 }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: sc.mcqPct >= 75 ? "#16a34a" : sc.mcqPct >= 50 ? "#d97706" : "#dc2626" }}>
                          {sc.mcqCorrect}<span style={{ fontSize: 12, fontWeight: 500, color: "#888" }}>/{sc.mcqTotal}</span>
                        </div>
                        <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>MCQ Correct</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#3730a3", marginTop: 1 }}>{sc.mcqPct}%</div>
                      </div>
                    )}

                    {/* Text/Code breakdown */}
                    {sc.tcTotal > 0 && (
                      <div className="text-center" style={{ minWidth: 80 }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: "#0d6efd" }}>
                          {sc.tcEarned}<span style={{ fontSize: 12, fontWeight: 500, color: "#888" }}>/{sc.tcMax}pts</span>
                        </div>
                        <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>Text/Code Score</div>
                        {sc.tcPending > 0 ? (
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#c2410c", marginTop: 1 }}>
                            <i className="fas fa-clock me-1" style={{ fontSize: 9 }}></i>{sc.tcPending} pending
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#166534", marginTop: 1 }}>
                            {sc.tcMax > 0 ? Math.round((sc.tcEarned / sc.tcMax) * 100) : 0}%
                          </div>
                        )}
                      </div>
                    )}

                    {/* Divider */}
                    <div style={{ width: 1, height: 60, background: "#e9d5ff", flexShrink: 0 }} />

                    {/* Meta info */}
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#1f2937", marginBottom: 6 }}>{selected.test_name}</div>
                      <div className="d-flex flex-wrap gap-3" style={{ fontSize: 12, color: "#555" }}>
                        <span><i className="fas fa-user me-1 text-purple"></i>{selected.name || "—"}</span>
                        <span><i className="fas fa-envelope me-1 text-purple"></i>{selected.email}</span>
                        <span><i className="fas fa-calendar me-1 text-purple"></i>{formatDateTime(selected.created_at)}</span>
                        <span>
                          <i className="fas fa-exclamation-triangle me-1" style={{ color: selected.violation_count > 0 ? "#dc2626" : "#16a34a" }}></i>
                          {selected.violation_count} Violation{selected.violation_count !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="mt-2">
                        <span style={{ display: "inline-block", padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: scoreBg(sc.overallPct), border: `1px solid ${scoreBorder(sc.overallPct)}`, color: scoreColor(sc.overallPct) }}>
                          {sc.overallPct >= EXCELLENCE_SCORE ? "Excellent" : sc.overallPct >= PASS_SCORE ? "Passed" : "Failed"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Answers tab ── */}
              {activeDetailTab === "answers" && (
                <div>
                  {detailLoading ? (
                    <div className="text-center text-muted py-4">
                      <i className="fas fa-spinner fa-spin fa-2x mb-2 d-block"></i>Loading answers…
                    </div>
                  ) : detailAnswers.length === 0 ? (
                    <div className="text-center text-muted py-4">No answers recorded.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {detailAnswers.map((ans, idx) => {
                        const qText = ans.question?.question || `Question ${idx + 1}`
                        const qType = ans.type ?? ans.question?.type
                        const isMcq = qType === 1
                        const isCode = qType === 3
                        const isCorrect = ans.isCorrect
                        const userAns = ans.user_answer ?? "—"
                        const correctAns = ans.correct_answer ?? "—"
                        const options = (() => {
                          try { return ans.question?.options ? JSON.parse(ans.question.options) : null } catch { return null }
                        })()

                        const borderCol = isCorrect === true ? "#86efac" : isCorrect === false ? "#fecaca" : "#e9d5ff"
                        const leftCol = isCorrect === true ? "#22c55e" : isCorrect === false ? "#ef4444" : "#a78bfa"
                        const bgCol = isCorrect === true ? "#f0fdf4" : isCorrect === false ? "#fff5f5" : "#faf5ff"

                        return (
                          <div key={idx} style={{ border: `1px solid ${borderCol}`, borderLeft: `4px solid ${leftCol}`, borderRadius: 8, padding: "12px 14px", background: bgCol }}>
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                              <div style={{ fontWeight: 600, fontSize: 13, color: "#1f2937", flex: 1 }}>
                                <span style={{ color: "#752a97", marginRight: 6 }}>Q{idx + 1}.</span>
                                <span className="badge me-1" style={{ background: isMcq ? "#0d6efd" : isCode ? "#6f42c1" : "#6c757d", fontSize: 10 }}>
                                  {isMcq ? "MCQ" : isCode ? "Code" : "Text"}
                                </span>
                                {qText}
                              </div>
                              <div style={{ whiteSpace: "nowrap" }}>
                                {isCorrect === true && (
                                  <span style={{ fontSize: 11, fontWeight: 600, color: "#16a34a", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 20, padding: "2px 8px" }}>
                                    {ans.ai_graded && !isMcq && <i className="fas fa-robot me-1" style={{ fontSize: 9 }}></i>}
                                    <i className="fas fa-check me-1"></i>Correct
                                  </span>
                                )}
                                {isCorrect === false && (
                                  <span style={{ fontSize: 11, fontWeight: 600, color: "#dc2626", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 20, padding: "2px 8px" }}>
                                    {ans.ai_graded && !isMcq && <i className="fas fa-robot me-1" style={{ fontSize: 9 }}></i>}
                                    <i className="fas fa-times me-1"></i>Wrong
                                  </span>
                                )}
                                {isCorrect === null && (
                                  <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 20, padding: "2px 8px" }}>
                                    Not Graded
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* MCQ options */}
                            {isMcq && options && (
                              <div className="d-flex flex-wrap gap-2 mb-2">
                                {Object.entries(options).map(([k, v]) => {
                                  const isUserPick = String(userAns) === String(k)
                                  const isCorrectPick = String(correctAns) === String(k)
                                  return (
                                    <span key={k} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, border: `1px solid ${isCorrectPick ? "#86efac" : isUserPick ? "#fca5a5" : "#e5e7eb"}`, background: isCorrectPick ? "#f0fdf4" : isUserPick ? "#fee2e2" : "#f9fafb", color: isCorrectPick ? "#15803d" : isUserPick ? "#b91c1c" : "#6b7280", fontWeight: isUserPick || isCorrectPick ? 600 : 400 }}>
                                      {k}. {v}{isUserPick && !isCorrectPick && " ✗"}{isCorrectPick && " ✓"}
                                    </span>
                                  )
                                })}
                              </div>
                            )}

                            {/* Text / Code answer */}
                            {!isMcq && (
                              userAns && String(userAns) !== "—" ? (
                                <pre style={{ background: isCode ? "#1e1e2e" : "#f8f8f8", color: isCode ? "#cdd6f4" : "#333", borderRadius: 6, padding: "10px 12px", fontSize: 12, fontFamily: isCode ? "monospace" : "inherit", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: "0 0 6px 0" }}>
                                  {String(userAns)}
                                </pre>
                              ) : (
                                <span style={{ color: "#aaa", fontSize: 12, fontStyle: "italic", display: "block", marginBottom: 6 }}>No answer provided</span>
                              )
                            )}

                            {/* MCQ selected text */}
                            {isMcq && (
                              <div style={{ fontSize: 12, color: "#555" }}>
                                <span style={{ fontWeight: 600, color: "#374151" }}>Selected: </span>
                                <span style={{ color: isCorrect === false ? "#dc2626" : "#374151" }}>{String(userAns)}</span>
                              </div>
                            )}

                            {/* Correct answer for wrong MCQ */}
                            {isMcq && isCorrect === false && (
                              <div style={{ fontSize: 12, color: "#16a34a", marginTop: 2 }}>
                                <span style={{ fontWeight: 600 }}>Correct: </span>{String(correctAns)}
                              </div>
                            )}

                            {/* AI explanation for Text/Code */}
                            {!isMcq && ans.ai_explanation && (
                              <div className="d-flex align-items-start gap-2 px-2 py-2 rounded mt-1"
                                style={{ background: "#f5f0ff", border: "1px solid #d8b4fe", fontSize: 11 }}>
                                <i className="fas fa-robot mt-1" style={{ color: "#752a97", fontSize: 12, minWidth: 13 }}></i>
                                <span style={{ color: "#444", lineHeight: 1.5 }}>{ans.ai_explanation}</span>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ── Violations tab ── */}
              {activeDetailTab === "violations" && (
                <div>
                  {selected.violation_count === 0 ? (
                    <div className="text-center py-4">
                      <i className="fas fa-shield-alt fa-2x mb-2 d-block" style={{ color: "#22c55e" }}></i>
                      <div style={{ fontWeight: 600, color: "#16a34a" }}>No violations detected</div>
                      <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>This candidate completed the test without any integrity issues.</div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {selected.violations.map((v, idx) => (
                        <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 14px", borderRadius: 8, background: "#fff5f5", border: "1px solid #fecaca", borderLeft: "4px solid #ef4444" }}>
                          <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="fas fa-exclamation-triangle" style={{ color: "#dc2626", fontSize: 11 }}></i>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: "#1f2937", textTransform: "capitalize" }}>
                              {(v.type || v.event || "Violation").replace(/_/g, " ")}
                            </div>
                            {v.timestamp || v.occurred_at || v.time ? (
                              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                                <i className="fas fa-clock me-1"></i>
                                {moment(v.timestamp || v.occurred_at || v.time).format("hh:mm:ss A")}
                              </div>
                            ) : null}
                            {v.question_index != null && (
                              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                                <i className="fas fa-question-circle me-1" style={{ color: "#752a97" }}></i>
                                On Question {v.question_index}
                              </div>
                            )}
                            {v.description && <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{v.description}</div>}
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 20, padding: "2px 8px" }}>#{idx + 1}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Modal>
        )
      })()}

      {/* Create Trainee Modal */}
      <Modal isOpen={traineeModal} size="xl" centered toggle={() => setTraineeModal(false)}>
        <div className="modal-header px-4 py-3">
          <h5 className="modal-title mt-0" style={{ fontSize: 15 }}>
            <i className="fas fa-user-graduate me-2 text-purple"></i>
            Create Trainee
          </h5>
          <button type="button" onClick={() => setTraineeModal(false)} className="btn-modal-close">
            <span aria-hidden="true" className="btn-modal-span">&times;</span>
          </button>
        </div>
        <div className="modal-body px-4 py-3">
          <form onSubmit={handleCreateTrainee}>
            {traineeSource && (
              <div className="mb-3" style={{ fontSize: 12, color: "#6b7280" }}>
                Creating from response by <strong>{traineeSource.name || traineeSource.email}</strong> ({traineeSource.test_name})
              </div>
            )}

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Name <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  value={traineeForm.name}
                  onChange={e => setTraineeForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Email <span className="text-danger">*</span></Label>
                <Input
                  type="email"
                  value={traineeForm.email}
                  onChange={e => setTraineeForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Phone</Label>
                <Input
                  type="text"
                  value={traineeForm.phone}
                  onChange={e => setTraineeForm(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>Password <span className="text-danger">*</span></Label>
                <Input
                  type="password"
                  value={traineeForm.password}
                  onChange={e => setTraineeForm(prev => ({ ...prev, password: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>Tier</Label>
              <div className="d-flex gap-2">
                {[{ key: "external", label: "External Trainee" }, { key: "internal", label: "Internal Trainee" }].map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTraineeForm(prev => ({ ...prev, tier: t.key }))}
                    style={{
                      padding: "5px 16px",
                      borderRadius: 20,
                      border: traineeForm.tier === t.key ? "none" : "1px solid #d8b4fe",
                      background: traineeForm.tier === t.key ? "linear-gradient(135deg, #752a97, #a855c9)" : "transparent",
                      color: traineeForm.tier === t.key ? "#fff" : "#752a97",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>Assign Exams</Label>
              <Select
                placeholder="Select exams…"
                className="basic-multi-select"
                classNamePrefix="select"
                options={examOptions}
                value={traineeForm.exams}
                onChange={val => setTraineeForm(prev => ({ ...prev, exams: val || [] }))}
                isMulti
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </div>

            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>Assign Training Docs</Label>
              <Select
                placeholder="Select training docs…"
                className="basic-multi-select"
                classNamePrefix="select"
                options={sopOptions}
                value={traineeForm.sops}
                onChange={val => setTraineeForm(prev => ({ ...prev, sops: val || [] }))}
                isMulti
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </div>

            <div className="mt-4 pt-3" style={{ borderTop: "1px solid #e9ecef" }}>
              <button type="submit" className="border-radius btn btn-purple btn-sm px-4" disabled={savingTrainee}>
                <i className="fas fa-user-graduate me-2"></i>
                {savingTrainee ? "Creating…" : "Create Trainee"}
              </button>
              <button type="button" className="border-radius btn btn-light btn-sm px-4 ms-2" onClick={() => setTraineeModal(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  )
}

export default ExternalCandidates
